import { composeEllipsisAriaLabelText } from '../strings.locstring.json';
import loc from 'owa-localize';
import { observer as observerLite } from 'mobx-react-lite';
/* tslint:disable:jsx-no-lambda WI:47713 */
import { CommandBar, ICommandBar } from '@fluentui/react/lib/CommandBar';
import { ControlIcons } from 'owa-control-icons';

import * as React from 'react';
import { IContextualMenuItem } from '@fluentui/react/lib/ContextualMenu';
import {
    SurfaceActionItem,
    onCommandBarDataReduced,
    onCommandBarDataGrown,
} from 'owa-surface-actions';
import { CommandBarButton, IButtonProps } from '@fluentui/react/lib/Button';
import { LazyProofingTeachingCallout } from 'owa-editor-proofing-plugin';
import { QuickUseIndicatorCalloutLazy } from 'owa-quickuse';

const styles = require('./ComposeBottom.scss');

export interface ComposeToolbarItem extends IContextualMenuItem {
    imageUrl?: string;
    customRender?: (className: string) => JSX.Element;
}

export interface ComposeBottomActionBarProps {
    items: SurfaceActionItem[];
    overflowItems: SurfaceActionItem[];
}

const ComposeToolbarButton = observerLite(function ComposeToolbarButton(props: IButtonProps) {
    const button = React.useRef<HTMLDivElement>();
    let isProofingButton: boolean = props.uniqueId === 'ProofingOptions';
    let isQuickUseButton: boolean = props.uniqueId === 'QuickUse';
    return (
        <>
            <div ref={button}>
                <CommandBarButton
                    {...props}
                    styles={{
                        root: styles.commandBarButton,
                        rootChecked: styles.checkedCommandBarButton,
                        rootDisabled: styles.disabledCommandBarButton,
                        menuIcon: styles.commandBarMenuIcon,
                    }}
                />
            </div>
            {isProofingButton && <LazyProofingTeachingCallout target={() => button.current} />}
            {isQuickUseButton && (
                <QuickUseIndicatorCalloutLazy
                    target={() => button.current}
                    trigger={(lightup: () => void) => {
                        // Trigger the callout display while a paste event first happened.
                        const onPaste = (e: ClipboardEvent) => {
                            lightup();
                        };
                        document.addEventListener('paste', onPaste);
                    }}
                />
            )}
        </>
    );
});

export interface ComposeBottomActionBarHandle {
    resize(): void;
}

/**
 * Hosts the custom surface actions in ComposeBottom.
 */
export default observerLite(
    function ComposeBottomActionBar(
        props: ComposeBottomActionBarProps,
        ref: React.Ref<ComposeBottomActionBarHandle>
    ) {
        React.useEffect(() => {
            return () => {
                commandBar.current = null;
            };
        }, []);
        const commandBar = React.useRef<ICommandBar>();

        React.useImperativeHandle(
            ref,
            () => ({
                resize() {
                    if (commandBar.current) {
                        commandBar.current.remeasure();
                    }
                },
            }),
            []
        );
        const { items, overflowItems } = props;
        return (
            <CommandBar
                componentRef={commandBar}
                items={items}
                overflowItems={overflowItems}
                overflowButtonProps={{
                    menuIconProps: {
                        iconName: ControlIcons.More,
                        styles: {
                            root: styles.commandBarOverflowIcon,
                        },
                    },
                    ariaLabel: loc(composeEllipsisAriaLabelText),
                }}
                buttonAs={ComposeToolbarButton}
                overflowButtonAs={ComposeToolbarButton}
                className={styles.surfaceActionsCommandBar}
                onDataReduced={onCommandBarDataReduced}
                onDataGrown={onCommandBarDataGrown}
            />
        );
    },
    { forwardRef: true }
);
